// Page Upgrade Pro - Passer de Particulier à Professionnel
// Vérification SIRET/SIREN avec IA + correspondance dirigeant

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, CheckCircle, XCircle, AlertCircle, Search,
  ArrowRight, ArrowLeft, Shield, Briefcase, MapPin,
  FileText, Loader2, User, BadgeCheck, Store, Sparkles,
  Clock, RefreshCw, MessageCircle, Lock, Eye, ChevronDown
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../../config/api';

// Liste des métiers et activités
const ACTIVITES_PRO = [
  // Métiers du bâtiment - Artisans
  { value: '', label: 'Sélectionne ton activité', category: null },
  { value: 'plombier', label: 'Plombier', category: 'artisan', icon: '💧' },
  { value: 'electricien', label: 'Électricien', category: 'artisan', icon: '⚡' },
  { value: 'macon', label: 'Maçon', category: 'artisan', icon: '🧱' },
  { value: 'carreleur', label: 'Carreleur', category: 'artisan', icon: '🔲' },
  { value: 'peintre', label: 'Peintre', category: 'artisan', icon: '🎨' },
  { value: 'menuisier', label: 'Menuisier', category: 'artisan', icon: '🔨' },
  { value: 'couvreur', label: 'Couvreur', category: 'artisan', icon: '🏠' },
  { value: 'chauffagiste', label: 'Chauffagiste', category: 'artisan', icon: '🔥' },
  { value: 'pisciniste', label: 'Pisciniste', category: 'artisan', icon: '🏊' },
  { value: 'paysagiste', label: 'Paysagiste', category: 'artisan', icon: '🌿' },
  { value: 'jardinier', label: 'Jardinier', category: 'artisan', icon: '🌱' },
  { value: 'climaticien', label: 'Climaticien', category: 'artisan', icon: '❄️' },
  { value: 'facadier', label: 'Façadier', category: 'artisan', icon: '🏢' },
  { value: 'serrurier', label: 'Serrurier', category: 'artisan', icon: '🔑' },
  { value: 'vitrier', label: 'Vitrier', category: 'artisan', icon: '🪟' },
  { value: 'charpentier', label: 'Charpentier', category: 'artisan', icon: '🪵' },
  { value: 'architecte', label: 'Architecte', category: 'artisan', icon: '📐' },
  { value: 'geometre', label: 'Géomètre', category: 'artisan', icon: '📏' },
  // Experts et bureaux d'études
  { value: 'expert_batiment', label: 'Expert bâtiment', category: 'expert', icon: '🔍' },
  { value: 'bureau_etudes', label: 'Bureau d\'études', category: 'expert', icon: '📊' },
  { value: 'diagnostiqueur', label: 'Diagnostiqueur immobilier', category: 'expert', icon: '📋' },
  { value: 'maitre_oeuvre', label: 'Maître d\'œuvre', category: 'expert', icon: '👷' },
  // Vendeurs / E-commerce
  { value: 'vendeur_materiaux', label: 'Vente de matériaux', category: 'vendeur', icon: '🧱' },
  { value: 'vendeur_outillage', label: 'Vente d\'outillage', category: 'vendeur', icon: '🔧' },
  { value: 'vendeur_sanitaire', label: 'Vente sanitaire / plomberie', category: 'vendeur', icon: '🚿' },
  { value: 'vendeur_electricite', label: 'Vente électricité', category: 'vendeur', icon: '💡' },
  { value: 'vendeur_carrelage', label: 'Vente carrelage / revêtements', category: 'vendeur', icon: '🔲' },
  { value: 'vendeur_peinture', label: 'Vente peinture / décoration', category: 'vendeur', icon: '🎨' },
  { value: 'vendeur_jardin', label: 'Vente jardin / extérieur', category: 'vendeur', icon: '🌳' },
  { value: 'vendeur_quincaillerie', label: 'Quincaillerie', category: 'vendeur', icon: '🔩' },
  { value: 'ecommerce_batiment', label: 'E-commerce bâtiment', category: 'vendeur', icon: '🛒' },
  // Services
  { value: 'prestataire_services', label: 'Prestataire de services', category: 'service', icon: '🛠️' },
  { value: 'autre', label: 'Autre activité', category: 'autre', icon: '📦' },
];

// Composant Step Indicator
function StepIndicator({ current, total = 3 }) {
  const steps = ['Infos entreprise', 'Vérification SIRET', 'Validation'];
  return (
    <div className="flex items-center justify-center gap-1 px-4 mb-6">
      {steps.map((label, i) => (
        <div key={i} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              current > i + 1 
                ? 'bg-emerald-500 text-white' 
                : current === i + 1 
                  ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30' 
                  : 'bg-gray-100 text-gray-400'
            }`}>
              {current > i + 1 ? <CheckCircle size={16} /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1 ${current === i + 1 ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-8 h-0.5 mx-1 mb-4 transition-colors ${current > i + 1 ? 'bg-emerald-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// Composant Info Card
function InfoCard({ icon: Icon, title, description, color = 'green' }) {
  const colors = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-emerald-500 to-teal-600',
    purple: 'from-purple-500 to-pink-600',
    orange: 'from-orange-500 to-amber-600',
  };
  return (
    <div className="flex items-start gap-3 p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-gray-100">
      <div className={`w-10 h-10 bg-gradient-to-br ${colors[color]} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="font-semibold text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  );
}

export default function UpgradeProPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    companyName: '',
    siret: '',
    address: '',
    postalCode: '',
    city: '',
    activity: '',
    phone: '',
  });
  
  // User data from localStorage
  const [userData, setUserData] = useState(null);
  
  const token = localStorage.getItem('ecosystem_token');
  
  useEffect(() => {
    // Load user data
    const ecosystemUser = localStorage.getItem('ecosystem_user');
    const ecosystemProfile = localStorage.getItem('ecosystem_profile');
    
    let user = null;
    if (ecosystemUser) {
      try {
        const parsed = JSON.parse(ecosystemUser);
        user = {
          name: `${parsed.first_name || ''} ${parsed.last_name || ''}`.trim() || parsed.name,
          firstName: parsed.first_name,
          lastName: parsed.last_name,
          email: parsed.email,
          isVerified: parsed.is_verified || false,
        };
      } catch(e) {}
    }
    
    if (!user && ecosystemProfile) {
      try {
        const parsed = JSON.parse(ecosystemProfile);
        user = {
          name: parsed.real_name || parsed.display_name,
          isVerified: parsed.is_verified || false,
        };
      } catch(e) {}
    }
    
    setUserData(user);
    
    // Check if user is verified
    if (user && !user.isVerified) {
      toast.error('Tu dois d\'abord vérifier ton identité');
      navigate('/reseau/verification');
    }
  }, [navigate]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format SIRET (remove spaces, keep only numbers)
    if (name === 'siret') {
      const formatted = value.replace(/\D/g, '').slice(0, 14);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Format SIRET for display
  const formatSiret = (siret) => {
    if (!siret) return '';
    return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4').trim();
  };

  // Submit for verification
  const submitVerification = async () => {
    if (!formData.companyName || !formData.siret || formData.siret.length < 9) {
      toast.error('Remplis tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setStep(2);
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/pro/verify-siret`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          company_name: formData.companyName,
          siret: formData.siret,
          address: formData.address,
          postal_code: formData.postalCode,
          city: formData.city,
          activity: formData.activity,
          phone: formData.phone,
        })
      });
      
      const data = await res.json();
      setVerificationResult(data);
      setStep(3);
      
      if (data.status === 'verified') {
        // Update local storage
        const profile = JSON.parse(localStorage.getItem('ecosystem_profile') || '{}');
        profile.is_pro = true;
        profile.company = data.company;
        localStorage.setItem('ecosystem_profile', JSON.stringify(profile));
        toast.success('Compte Pro activé !');
      }
    } catch (err) {
      setVerificationResult({ 
        status: 'error', 
        message: 'Erreur de connexion au serveur' 
      });
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const reset = () => {
    setFormData({
      companyName: '',
      siret: '',
      address: '',
      postalCode: '',
      city: '',
      activity: '',
      phone: '',
    });
    setVerificationResult(null);
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => step > 1 && step < 3 ? setStep(step - 1) : navigate(-1)}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Briefcase size={18} className="text-white" />
            </div>
            <span className="font-bold text-gray-900">Passer Pro</span>
          </div>
          <div className="w-10" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {step < 3 && <StepIndicator current={step} />}

        <AnimatePresence mode="wait">
          {/* ═══ ÉTAPE 1: INFORMATIONS ENTREPRISE ═══ */}
          {step === 1 && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-5"
            >
              {/* Hero */}
              <div className="text-center py-4">
                <div className="relative inline-block mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-xl shadow-emerald-500/30">
                    <Store size={40} className="text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-4 border-white">
                    <BadgeCheck size={14} className="text-white" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Deviens vendeur Pro
                </h1>
                <p className="text-gray-500">
                  Crée ta boutique professionnelle vérifiée
                </p>
              </div>

              {/* User verified badge */}
              {userData && (
                <div className="bg-emerald-50 rounded-xl p-3 flex items-center gap-3 border border-emerald-100">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-emerald-800 text-sm">{userData.name}</p>
                    <p className="text-xs text-emerald-600">Identité vérifiée ✓</p>
                  </div>
                  <Shield size={20} className="text-emerald-500" />
                </div>
              )}

              {/* Form */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 size={18} className="text-emerald-600" />
                  Informations de l'entreprise
                </h3>

                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Ex: Dupont Rénovation SARL"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* SIRET */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SIRET ou SIREN *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="siret"
                      value={formatSiret(formData.siret)}
                      onChange={handleChange}
                      placeholder="123 456 789 00012"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-mono"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      {formData.siret.length}/14
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    SIREN (9 chiffres) ou SIRET (14 chiffres)
                  </p>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse du siège
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="12 rue de la Paix"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* City + Postal Code */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      placeholder="75001"
                      maxLength={5}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Paris"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Activity - Menu déroulant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ton activité *
                  </label>
                  <div className="relative">
                    <select
                      name="activity"
                      value={formData.activity}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                    >
                      {ACTIVITES_PRO.map((act) => (
                        <option key={act.value} value={act.value} disabled={!act.value}>
                          {act.icon ? `${act.icon} ${act.label}` : act.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                  {formData.activity && (
                    <p className="text-xs text-emerald-600 mt-1">
                      {ACTIVITES_PRO.find(a => a.value === formData.activity)?.category === 'artisan' && '🔧 Artisan du bâtiment'}
                      {ACTIVITES_PRO.find(a => a.value === formData.activity)?.category === 'expert' && '🔍 Expert / Bureau d\'études'}
                      {ACTIVITES_PRO.find(a => a.value === formData.activity)?.category === 'vendeur' && '🛒 Vendeur / E-commerce'}
                      {ACTIVITES_PRO.find(a => a.value === formData.activity)?.category === 'service' && '🛠️ Prestataire de services'}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone professionnel
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Verification explanation */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Eye size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-emerald-800 mb-1">Vérification automatique</h3>
                    <p className="text-sm text-emerald-700 leading-relaxed">
                      Notre IA va vérifier ton SIRET dans les registres officiels 
                      et confirmer que <strong>tu es bien le dirigeant</strong> de cette entreprise.
                    </p>
                  </div>
                </div>
              </div>

              {/* Protocol */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Search size={18} className="text-emerald-600" />
                  Ce que nous vérifions
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    Existence de l'entreprise (INSEE, Infogreffe)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    Statut actif (non radiée, non en liquidation)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    Correspondance dirigeant / ton identité vérifiée
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <Button
                onClick={submitVerification}
                disabled={!formData.companyName || formData.siret.length < 9}
                className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-2xl font-semibold text-base shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Vérifier mon entreprise
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </motion.div>
          )}

          {/* ═══ ÉTAPE 2: VÉRIFICATION EN COURS ═══ */}
          {step === 2 && (
            <motion.div
              key="verifying"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="py-12 text-center"
            >
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-ping opacity-20" />
                <div className="absolute inset-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse opacity-40" />
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center shadow-xl shadow-emerald-500/30">
                  <Search size={40} className="text-white animate-pulse" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Vérification en cours...
              </h2>
              <p className="text-gray-500 mb-6">
                Recherche dans les registres officiels
              </p>

              <div className="max-w-xs mx-auto space-y-3">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                  <CheckCircle size={18} className="text-emerald-500" />
                  <span className="text-sm text-emerald-800">SIRET reçu</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl animate-pulse">
                  <Loader2 size={18} className="text-emerald-500 animate-spin" />
                  <span className="text-sm text-emerald-800">Recherche INSEE / Infogreffe...</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Clock size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-500">Vérification dirigeant...</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══ ÉTAPE 3: RÉSULTAT ═══ */}
          {step === 3 && verificationResult && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="py-8"
            >
              {/* VÉRIFIÉ */}
              {verificationResult.status === 'verified' && (
                <div className="text-center">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-20" />
                    <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-xl shadow-emerald-500/30">
                      <CheckCircle size={48} className="text-white" />
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Compte Pro activé ! 🎉
                  </h2>
                  <p className="text-gray-500 mb-6">
                    Ton entreprise est maintenant vérifiée
                  </p>

                  {/* Company info */}
                  {verificationResult.company && (
                    <div className="bg-white rounded-2xl p-5 mb-6 border border-gray-100 text-left">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                          <Building2 size={24} className="text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{verificationResult.company.name}</p>
                          <p className="text-sm text-gray-500">SIRET: {formatSiret(verificationResult.company.siret)}</p>
                        </div>
                      </div>
                      
                      {verificationResult.company.address && (
                        <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                          <MapPin size={16} className="text-gray-400 mt-0.5" />
                          <span>{verificationResult.company.address}</span>
                        </div>
                      )}
                      
                      {verificationResult.company.activity && (
                        <div className="flex items-start gap-2 text-sm text-gray-600">
                          <Briefcase size={16} className="text-gray-400 mt-0.5" />
                          <span>{verificationResult.company.activity}</span>
                        </div>
                      )}

                      {verificationResult.dirigeant_match && (
                        <div className="mt-4 p-3 bg-emerald-50 rounded-xl flex items-center gap-2">
                          <BadgeCheck size={18} className="text-emerald-600" />
                          <span className="text-sm text-emerald-800">
                            Dirigeant confirmé : {verificationResult.dirigeant_name}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-emerald-50 rounded-2xl p-5 mb-6 border border-emerald-100">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Store size={24} className="text-emerald-600" />
                      <span className="font-bold text-emerald-800">Ta boutique Pro est prête</span>
                    </div>
                    <p className="text-sm text-emerald-700">
                      Tu peux maintenant créer des annonces professionnelles avec ton badge vérifié.
                    </p>
                  </div>

                  <Button
                    onClick={() => navigate('/cockpit-pro')}
                    className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-2xl font-semibold shadow-lg shadow-emerald-500/30"
                  >
                    Accéder au Cockpit Pro
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </div>
              )}

              {/* VÉRIFICATION MANUELLE */}
              {verificationResult.status === 'manual_review' && (
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-amber-500/30">
                    <Clock size={48} className="text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Vérification en cours
                  </h2>
                  <p className="text-gray-500 mb-6">
                    {verificationResult.message || "Notre équipe va examiner ta demande"}
                  </p>

                  <div className="bg-amber-50 rounded-2xl p-5 mb-6 border border-amber-100">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <AlertCircle size={24} className="text-amber-600" />
                      <span className="font-bold text-amber-800">Examen manuel requis</span>
                    </div>
                    <p className="text-sm text-amber-700">
                      {verificationResult.reason || "Nous n'avons pas pu confirmer automatiquement que tu es le dirigeant. Notre équipe va vérifier manuellement sous 24-48h."}
                    </p>
                  </div>

                  {verificationResult.required_documents && (
                    <div className="bg-white rounded-2xl p-5 mb-6 border border-gray-100 text-left">
                      <p className="font-semibold text-gray-900 mb-3">Documents pouvant accélérer la vérification :</p>
                      <ul className="space-y-2">
                        {verificationResult.required_documents.map((doc, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                            <FileText size={16} className="text-amber-500" />
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={() => navigate('/cockpit-particulier')}
                    className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-2xl font-semibold shadow-lg shadow-amber-500/30"
                  >
                    Retour au Cockpit
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </div>
              )}

              {/* REJETÉ */}
              {(verificationResult.status === 'rejected' || verificationResult.status === 'error') && (
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-500/30">
                    <XCircle size={48} className="text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Vérification non réussie
                  </h2>
                  <p className="text-gray-500 mb-6">
                    {verificationResult.message || "Nous n'avons pas pu vérifier ton entreprise"}
                  </p>

                  {verificationResult.issues && verificationResult.issues.length > 0 && (
                    <div className="bg-red-50 rounded-2xl p-5 mb-6 border border-red-100 text-left">
                      <p className="font-bold text-red-800 mb-2">Problèmes détectés :</p>
                      <ul className="space-y-1">
                        {verificationResult.issues.map((issue, i) => (
                          <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                            <span>•</span> {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Button
                      onClick={reset}
                      className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-2xl font-semibold shadow-lg shadow-emerald-500/30"
                    >
                      <RefreshCw size={20} className="mr-2" />
                      Réessayer
                    </Button>
                    
                    <button
                      onClick={() => navigate('/support/ticket')}
                      className="w-full h-12 bg-white border-2 border-gray-200 text-gray-700 rounded-2xl font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                    >
                      <MessageCircle size={18} />
                      Ouvrir un ticket support
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
